import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Camera, Shield, Wifi, Home as HomeIcon, Server,
  Wrench, Headphones, CheckCircle, Star, ChevronRight, Play,
  Cpu, Zap, Eye, Lock, Wifi as WifiIcon, Sun, Smartphone, Radio,
  Activity, MapPin, Users, Layers, ShoppingCart
} from "lucide-react";
import BuyNowModal from "../components/BuyNowModal";

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

function useParallax() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 24;
      const y = (clientY / innerHeight - 0.5) * 16;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);
  return ref;
}

const services = [
  { icon: Camera, title: "CCTV Systems", desc: "Professional surveillance with 4K clarity and smart analytics." },
  { icon: Shield, title: "Alarm Systems", desc: "Advanced intrusion detection for homes and businesses." },
  { icon: HomeIcon, title: "Smart Home", desc: "Intelligent automation for lighting, locks, and more." },
  { icon: Wifi, title: "Starlink Internet", desc: "High-speed satellite internet for remote Sri Lanka." },
  { icon: Server, title: "Networking", desc: "Enterprise-grade WiFi and structured cabling." },
  { icon: Wrench, title: "Maintenance", desc: "Annual service contracts and 24/7 technical support." },
  { icon: Headphones, title: "24/7 Support", desc: "Round-the-clock expert technical assistance." },
  { icon: Zap, title: "Installation", desc: "Professional setup and configuration services." },
];

const brands = [
  { name: "Dahua", color: "#0066CC" },
  { name: "Hikvision", color: "#E83000" },
  { name: "EZVIZ", color: "#FF6B00" },
  { name: "TP-Link", color: "#4CAF50" },
  { name: "Ruijie", color: "#1565C0" },
  { name: "Ubiquiti", color: "#1A1A2E" },
  { name: "Starlink", color: "#00A6FF" },
];

const packages = [
  {
    name: "4 Camera System",
    price: "Rs.100,000",
    numPrice: 100000,
    features: [
      "4 Full Color Cameras", "8 Channel DVR", "500GB HDD",
      "CAT6 Cable & Power Cable", "Video Baluns & HDMI",
      "Rack Cabinet & USB Extension", "Professional Installation",
      "Mobile Live View", "Monitor View Setup",
      "2 Year Product Warranty", "3 Year Service Warranty",
    ],
    popular: false,
  },
  {
    name: "8 Camera System",
    price: "Rs.160,000",
    numPrice: 160000,
    features: [
      "8 Full Color Cameras", "16 Channel DVR", "1TB HDD",
      "CAT6 Cable & Power Cable", "Video Baluns & HDMI",
      "Rack Cabinet & USB Extension", "Professional Installation",
      "Mobile Live View", "Monitor View Setup",
      "2 Year Product Warranty", "3 Year Service Warranty",
    ],
    popular: true,
  },
  {
    name: "16 Camera System",
    price: "Rs.310,000",
    numPrice: 310000,
    features: [
      "16 Full Color Cameras", "16 Channel DVR", "2TB HDD",
      "CAT6 Cable & Power Cable", "Video Baluns & HDMI",
      "Rack Cabinet & USB Extension", "Professional Installation",
      "Mobile Live View", "Monitor View Setup",
      "2 Year Product Warranty", "3 Year Service Warranty",
      "Enterprise Support Package",
    ],
    popular: false,
  },
];

const smartCameras = [
  { icon: WifiIcon, name: "WiFi Cameras" },
  { icon: Radio, name: "4G SIM Cameras" },
  { icon: Sun, name: "Solar Cameras" },
  { icon: Eye, name: "PT Cameras" },
  { icon: HomeIcon, name: "Indoor Cameras" },
  { icon: Lock, name: "Outdoor Cameras" },
];

const smartHomeFeatures = [
  "Smart Lighting", "Smart Switches", "Smart Locks", "Smart Curtains",
  "Voice Control", "Mobile Control", "Energy Monitoring",
];

export default function Home() {
  const ref = useInView();
  const parallaxRef = useParallax();
  const [aiAlert, setAiAlert] = useState(false);
  const [pkgBuyNow, setPkgBuyNow] = useState<{ id: string; name: string; type: 'product' | 'package'; price: number; source?: string } | null>(null);

  useEffect(() => {
    // Toggle AI alert every 4 seconds for realistic effect
    const t = setInterval(() => setAiAlert(v => !v), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div ref={ref}>
      {/* ─── HERO ────────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #FAFAFA 0%, #F5F5F7 40%, #FFFFFF 100%)' }}>

        {/* Background layers */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Warm orange bloom — bottom left */}
          <div className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(127,168,106,0.12) 0%, transparent 70%)', filter: 'blur(1px)' }} />
          {/* Cool dark bloom — top right */}
          <div className="absolute -top-40 -right-40 w-[800px] h-[800px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(29,29,31,0.06) 0%, transparent 65%)' }} />
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.025]"
            style={{ backgroundImage: 'linear-gradient(#1D1D1F 1px, transparent 1px), linear-gradient(90deg, #1D1D1F 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>

        <div className="container-custom mx-auto px-6 sm:px-8 lg:px-12 xl:px-24 pt-32 pb-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">

            {/* ─── LEFT COLUMN ─── */}
            <div>
              {/* Badge */}
              <div className="animate-on-scroll inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-8"
                style={{ background: 'rgba(127,168,106,0.08)', border: '1px solid rgba(127,168,106,0.2)' }}>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-orange opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-orange" />
                </span>
                <span className="text-sm font-medium text-brand-orange tracking-wide">Professional Solutions · Sri Lanka</span>
              </div>

              {/* Headline */}
              <h1 className="animate-on-scroll stagger-1 font-display font-bold tracking-tight leading-[1.04] mb-6"
                style={{ fontSize: 'clamp(2.8rem, 5vw, 4.5rem)' }}>
                Intelligent Security.<br />
                <span className="gradient-text">For Modern Living.</span>
              </h1>

              {/* Subtitle */}
              <p className="animate-on-scroll stagger-2 text-[1.05rem] sm:text-lg text-brand-muted leading-[1.75] max-w-lg mb-10">
                Professional CCTV, Alarm Systems, Smart Home Automation, Starlink Internet and Enterprise Networking Solutions for Homes and Businesses Across Sri Lanka.
              </p>

              {/* CTA Buttons */}
              <div className="animate-on-scroll stagger-3 flex flex-col sm:flex-row gap-3.5 mb-14">
                <Link to="/contact" className="btn-primary group">
                  Get Free Consultation
                  <ArrowRight size={17} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/services"
                  className="inline-flex items-center justify-center px-7 py-4 rounded-2xl font-semibold text-base transition-all duration-300 group"
                  style={{ background: 'rgba(29,29,31,0.06)', color: '#1D1D1F', border: '1px solid rgba(29,29,31,0.12)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(29,29,31,0.1)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(29,29,31,0.06)'; }}>
                  Explore Solutions
                  <ChevronRight size={16} className="ml-1.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>

              {/* Stats — 4 premium metric cards */}
              <div className="animate-on-scroll stagger-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { value: '500+', label: 'Projects Completed', icon: Layers, color: '#7FA86A' },
                  { value: '7+', label: 'Years Experience', icon: Star, color: '#8B5CF6' },
                  { value: '24/7', label: 'Technical Support', icon: Activity, color: '#10B981' },
                  { value: '3yr', label: 'Service Warranty', icon: Shield, color: '#3B82F6' },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="group relative rounded-2xl p-4 transition-all duration-300 cursor-default"
                      style={{ background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(232,232,237,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 2px 16px rgba(0,0,0,0.04)' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)'; }}>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center mb-2.5"
                        style={{ background: `${s.color}18` }}>
                        <Icon size={14} style={{ color: s.color }} />
                      </div>
                      <p className="text-2xl font-bold text-brand-dark tracking-tight">{s.value}</p>
                      <p className="text-[11px] font-medium text-brand-muted mt-0.5 leading-snug">{s.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ─── RIGHT COLUMN — Security Dashboard Visual ─── */}
            <div ref={parallaxRef} className="animate-on-scroll stagger-2 relative transition-transform duration-75 ease-out will-change-transform">

              {/* Main dark dashboard card */}
              <div className="relative rounded-[2.5rem] overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #1a1a1e 0%, #0f0f12 60%, #1D1D1F 100%)',
                  boxShadow: '0 40px 120px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.08)',
                  padding: '24px',
                }}>

                {/* Dashboard header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-white/90 text-[13px] font-semibold tracking-wide">K-TEC SECURITY</p>
                    <p className="text-white/35 text-[10px] mt-0.5">Live Monitoring · 4 Cameras</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                      style={{ background: 'rgba(16,185,129,0.15)', color: '#34D399', border: '1px solid rgba(52,211,153,0.2)' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      LIVE
                    </span>
                  </div>
                </div>

                {/* Camera grid — 2×2 */}
                <div className="grid grid-cols-2 gap-2.5 mb-4">
                  {[
                    { label: 'Front Gate', active: true, aiAlert: aiAlert },
                    { label: 'Living Room', active: true, aiAlert: false },
                    { label: 'Car Park', active: true, aiAlert: false },
                    { label: 'Back Garden', active: false, aiAlert: false },
                  ].map((cam, i) => (
                    <div key={i} className="relative rounded-2xl overflow-hidden group"
                      style={{ aspectRatio: '16/10', background: 'linear-gradient(135deg, #1e1e24 0%, #15151a 100%)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      {/* Camera scene illustration */}
                      <div className="absolute inset-0 flex items-end justify-center pb-3">
                        {/* Ground plane */}
                        <div className="absolute bottom-0 left-0 right-0 h-1/3"
                          style={{ background: 'linear-gradient(0deg, rgba(127,168,106,0.06) 0%, transparent 100%)' }} />
                        {/* Perspective grid lines */}
                        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 160 100">
                          <line x1="80" y1="30" x2="0" y2="100" stroke="#7FA86A" strokeWidth="0.5" />
                          <line x1="80" y1="30" x2="160" y2="100" stroke="#7FA86A" strokeWidth="0.5" />
                          <line x1="80" y1="30" x2="80" y2="100" stroke="#7FA86A" strokeWidth="0.5" />
                          <line x1="80" y1="30" x2="40" y2="100" stroke="#7FA86A" strokeWidth="0.3" />
                          <line x1="80" y1="30" x2="120" y2="100" stroke="#7FA86A" strokeWidth="0.3" />
                          <ellipse cx="80" cy="75" rx="40" ry="6" stroke="#7FA86A" strokeWidth="0.4" fill="none" opacity="0.5" />
                        </svg>
                        {/* Camera icon */}
                        <Camera size={18} className="relative z-10 opacity-20 mb-1" style={{ color: '#7FA86A' }} />
                      </div>

                      {/* AI detection box (cam 0 only when alert) */}
                      {i === 0 && cam.aiAlert && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-12 rounded-lg"
                            style={{ border: '1.5px solid #7FA86A', boxShadow: '0 0 12px rgba(127,168,106,0.4)', animation: 'pulse 1.5s ease-in-out infinite' }}>
                            <div className="absolute -top-3.5 left-0 text-[8px] font-bold px-1.5 py-0.5 rounded"
                              style={{ background: '#7FA86A', color: '#fff', letterSpacing: '0.05em' }}>MOTION</div>
                          </div>
                        </div>
                      )}

                      {/* Corner brackets on hover */}
                      <div className="absolute top-2 left-2 w-3 h-3 opacity-40"
                        style={{ borderTop: '1.5px solid #7FA86A', borderLeft: '1.5px solid #7FA86A' }} />
                      <div className="absolute top-2 right-2 w-3 h-3 opacity-40"
                        style={{ borderTop: '1.5px solid #7FA86A', borderRight: '1.5px solid #7FA86A' }} />

                      {/* Label */}
                      <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between">
                        <span className="text-[8px] font-semibold text-white/50 uppercase tracking-wider">{cam.label}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${cam.active ? 'bg-emerald-400' : 'bg-white/20'}`}
                          style={cam.active ? { boxShadow: '0 0 6px #34D399' } : {}} />
                      </div>

                      {/* Recording indicator */}
                      {cam.active && (
                        <div className="absolute top-1.5 left-2 flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-[7px] font-bold text-red-400 tracking-wider">REC</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* AI Intelligence bar */}
                <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(127,168,106,0.08)', border: '1px solid rgba(127,168,106,0.12)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'rgba(127,168,106,0.2)' }}>
                        <Cpu size={11} className="text-brand-orange" />
                      </div>
                      <span className="text-[11px] font-semibold text-white/80">AI Motion Detection</span>
                    </div>
                    <span className="text-[10px] font-bold text-brand-orange">98.4%</span>
                  </div>
                  <div className="w-full h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div className="h-1 rounded-full transition-all duration-1000"
                      style={{ width: '98%', background: 'linear-gradient(90deg, #7FA86A, #96BE82)' }} />
                  </div>
                  <p className="text-[9px] text-white/35 mt-1.5">No intrusion detected · Last scan 2s ago</p>
                </div>

                {/* Bottom row: 3 mini stats */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Cameras', value: '4/4', color: '#34D399' },
                    { label: 'Storage', value: '847GB', color: '#60A5FA' },
                    { label: 'Uptime', value: '99.9%', color: '#A78BFA' },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl p-2.5 text-center"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-[13px] font-bold" style={{ color: s.color }}>{s.value}</p>
                      <p className="text-[9px] text-white/35 font-medium uppercase tracking-wider mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating card — System Active */}
              <div className="absolute -bottom-6 -left-8 rounded-2xl p-3.5 shadow-xl z-20"
                style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(24px)', border: '1px solid rgba(232,232,237,0.8)', boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.6)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)' }}>
                    <CheckCircle size={18} className="text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-[12px] font-bold text-brand-dark">All Systems Active</p>
                    <p className="text-[10px] text-brand-muted">4 cameras · Online</p>
                  </div>
                </div>
              </div>

              {/* Floating card — Mobile App */}
              <div className="absolute -top-5 -right-6 rounded-2xl p-3.5 shadow-xl z-20"
                style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(24px)', border: '1px solid rgba(232,232,237,0.8)', boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.6)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(127,168,106,0.1)' }}>
                    <Smartphone size={18} className="text-brand-orange" />
                  </div>
                  <div>
                    <p className="text-[12px] font-bold text-brand-dark">Live on Mobile</p>
                    <p className="text-[10px] text-brand-muted">Anywhere access</p>
                  </div>
                </div>
              </div>

              {/* Glow behind card */}
              <div className="absolute inset-0 -z-10 rounded-[3rem]"
                style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(127,168,106,0.18) 0%, transparent 70%)', filter: 'blur(24px)' }} />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-muted">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-brand-muted to-transparent" />
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">What We Do</p>
            <h2 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Complete Technology Solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div key={i} className={`animate-on-scroll stagger-${(i % 4) + 1} card-premium group cursor-pointer`}>
                <div className="w-14 h-14 bg-brand-orange/10 rounded-2xl flex items-center justify-center mb-5 group-hover:bg-brand-orange group-hover:scale-110 transition-all duration-300">
                  <s.icon size={24} className="text-brand-orange group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Brands */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Trusted Partners</p>
            <h2 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl font-bold tracking-tight">
              World-Class Brands
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-6">
            {brands.map((b, i) => (
              <div key={i} className="animate-on-scroll stagger-1 bg-white rounded-2xl p-6 flex items-center justify-center h-24 overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1">
                <span className="font-display font-bold text-base tracking-tight" style={{ color: b.color }}>{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">CCTV Packages</p>
            <h2 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Choose Your Security Package
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, i) => (
              <div key={i} className={`animate-on-scroll stagger-${i + 1} relative bg-white rounded-3xl p-8 shadow-card border-2 transition-all duration-500 hover:shadow-card-hover hover:-translate-y-2 ${pkg.popular ? "border-brand-orange shadow-orange" : "border-transparent"}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-orange text-white text-xs font-bold px-4 py-1.5 rounded-full z-10">
                    Most Popular
                  </div>
                )}
                <h3 className="font-display text-xl font-bold mb-2">{pkg.name}</h3>
                <p className="text-4xl font-bold text-brand-orange mb-6">{pkg.price}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm">
                      <CheckCircle size={16} className="text-brand-orange mt-0.5 shrink-0" />
                      <span className="text-brand-dark/70">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setPkgBuyNow({ id: `pkg-${i}`, name: pkg.name, type: 'package', price: pkg.numPrice, source: 'home_packages' })}
                    className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all ${pkg.popular ? 'bg-brand-orange text-white hover:bg-brand-orange-dark' : 'bg-brand-orange text-white hover:bg-brand-orange-dark'}`}
                  >
                    <ShoppingCart size={15} /> Buy Now
                  </button>
                  <Link to="/contact" className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-semibold text-sm border border-gray-300 text-gray-700 hover:border-brand-orange hover:text-brand-orange transition-all">
                    Get a Quote <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Cameras */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Smart Cameras</p>
            <h2 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Camera Categories
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {smartCameras.map((c, i) => (
              <Link key={i} to="/products" className="animate-on-scroll stagger-1 bg-white rounded-2xl p-6 text-center overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 group">
                <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-brand-orange group-hover:scale-110 transition-all">
                  <c.icon size={22} className="text-brand-orange group-hover:text-white transition-colors" />
                </div>
                <p className="text-sm font-semibold">{c.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Home */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Smart Home</p>
              <h2 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl font-bold tracking-tight mb-6">
                Intelligent Living<br /><span className="gradient-text">Redefined</span>
              </h2>
              <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed mb-8">
                Transform your home with cutting-edge smart technology. Control everything from lighting to locks with your voice or smartphone.
              </p>
              <div className="animate-on-scroll stagger-3 grid grid-cols-2 gap-3">
                {smartHomeFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-brand-orange" />
                    <span className="text-sm font-medium">{f}</span>
                  </div>
                ))}
              </div>
              <div className="animate-on-scroll stagger-4 mt-8">
                <Link to="/products" className="btn-primary">
                  Explore Smart Home
                  <ArrowRight size={18} className="ml-2" />
                </Link>
              </div>
            </div>
            <div className="animate-on-scroll stagger-2">
              <div className="relative rounded-4xl overflow-hidden bg-gradient-to-br from-brand-dark to-gray-900 aspect-[4/3]">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <HomeIcon size={64} className="text-brand-orange/30 mx-auto mb-4" />
                    <p className="text-white/40 text-sm">Smart Home Control Center</p>
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">Living Room</p>
                      <p className="text-xs text-brand-muted">3 lights, 2 switches</p>
                    </div>
                    <div className="w-12 h-6 bg-brand-orange rounded-full relative">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Starlink & Networking */}
      <section className="section-padding bg-brand-dark text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-on-scroll">
              <div className="relative rounded-4xl overflow-hidden bg-white/5 aspect-[4/3] flex items-center justify-center">
                <div className="text-center">
                  <Wifi size={64} className="text-brand-orange/30 mx-auto mb-4" />
                  <p className="text-white/40 text-sm">Starlink & Network Hub</p>
                </div>
              </div>
            </div>
            <div>
              <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Connectivity</p>
              <h2 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl font-bold tracking-tight mb-6">
                Starlink Internet<br />& Enterprise Networking
              </h2>
              <p className="animate-on-scroll stagger-2 text-lg text-white/60 leading-relaxed mb-8">
                High-speed satellite internet and enterprise-grade networking solutions. From rural connectivity to campus-wide WiFi.
              </p>
              <div className="animate-on-scroll stagger-3 space-y-4">
                {[
                  { icon: Wifi, text: "Starlink Installation & Configuration" },
                  { icon: Server, text: "Enterprise WiFi & Access Points" },
                  { icon: Cpu, text: "Structured Cabling & Fiber" },
                  { icon: Wrench, text: "Network Management" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                      <item.icon size={18} className="text-brand-orange" />
                    </div>
                    <span className="text-white/80">{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="animate-on-scroll stagger-4 mt-8">
                <Link to="/products" className="btn-primary">
                  Learn More
                  <ArrowRight size={18} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="animate-on-scroll font-display text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Ready to Upgrade<br />Your Security?
            </h2>
            <p className="animate-on-scroll stagger-1 text-lg text-brand-muted mb-10">
              Get a free consultation and customized solution for your home or business. Our experts are ready to help.
            </p>
            <div className="animate-on-scroll stagger-2 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Get Free Consultation
                <ArrowRight size={18} className="ml-2" />
              </Link>
              <a href="https://wa.me/94702161323" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Package Buy Now Modal */}
      {pkgBuyNow && (
        <BuyNowModal
          isOpen={true}
          onClose={() => setPkgBuyNow(null)}
          item={pkgBuyNow}
        />
      )}
    </div>
  );
}
